import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../../lib/api";
import { Card, btnPrimary, btnSecondary } from "../ui-bits";
import { toast } from "sonner";
import {
  Plus, Tv, Trash2, Settings2, Video, ChevronRight, Eye, Film, Lock, AlertTriangle,
  Crown, Youtube, Rocket, Flame, TrendingUp, Sparkles, Loader2, GripVertical,
  CalendarDays, CalendarClock, Send, Wand2, Clock,
} from "lucide-react";

const fmtCompact = (n) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(n || 0);
const fmtUsd = (n) => `$${Math.round(n).toLocaleString()}`;

// Estimación de ganancias mensuales — NUNCA viene de la API de YouTube (ni monetizado se puede
// sacar sin el scope especial yt-analytics-monetary.readonly, y sin monetizar el dato
// simplemente no existe). Rango de RPM ($ por 1.000 vistas) para canales documentales/faceless
// en 2026, contrastado en varias fuentes del sector (Nexlev, Faceless.my, Kineclip): $5-15,
// se usa $5-12 aquí como rango algo conservador para no sobreprometer. Es SOLO orientativo —
// el RPM real depende muchísimo de la geografía de la audiencia, la duración y la temporada.
const DOC_RPM_MIN = 5;
const DOC_RPM_MAX = 12;
import ChannelWizard from "./ChannelWizard";
import ChannelKeepSelector from "../ChannelKeepSelector";
import { useQuota } from "../../context/QuotaContext";

const COLORS = ["bg-blue-600", "bg-rose-600", "bg-emerald-600", "bg-amber-600", "bg-indigo-600", "bg-cyan-700"];

// "Tramo de arranque": umbral no oficial (documentado en varias guías de creadores) a partir
// del cual YouTube empieza a tener suficiente señal para recomendar un canal nuevo. No es una
// cifra mágica del algoritmo real — es una expectativa razonable para dar constancia al usuario.
const STARTUP_MILESTONE = 12;
// Días sin crear un vídeo nuevo en el canal a partir de los cuales avisamos de que la racha se
// enfría — deliberadamente generoso (no todos publican semanalmente).
const STALE_DAYS = 14;

const INSIGHT_TONES = {
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
  blue: "bg-blue-50 text-blue-800 border-blue-200",
  violet: "bg-violet-50 text-violet-800 border-violet-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function daysSince(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) ? d : null;
}

// Qué está pasando AHORA MISMO en el canal (viene precalculado del backend, ver GET /channels
// → activity_by_channel): un vídeo fallado, uno esperando tu revisión (1-clic pausado) o uno
// en curso. Tiene prioridad absoluta sobre los avisos de crecimiento de abajo — mientras algo
// esté roto, pendiente de aprobar o cocinándose, no tiene sentido distraer con "prueba el
// calendario" o "llevas N días sin publicar".
function getActivity(ch, t) {
  const a = ch.activity;
  if (!a) return null;
  // project_id viene ya resuelto del backend (_project_activity, ver server.py) en los 4
  // tipos — pero se quedaba sin copiar aquí, así que el clic SIEMPRE caía al fallback de abrir
  // el canal entero (ver openCh más abajo en ChannelCard) en vez de entrar directo al vídeo
  // concreto que falló/espera revisión, pese a que openChannelProject ya estaba construido
  // exactamente para eso (ver el comentario en Builder.jsx).
  if (a.kind === "failed") {
    return {
      tone: "rose", Icon: AlertTriangle, project_id: a.project_id,
      text: t("channels.activityFailed", { name: a.name }),
      cta: t("channels.activityFailedCta"),
    };
  }
  if (a.kind === "awaiting_review") {
    return {
      tone: "amber", Icon: Eye, project_id: a.project_id,
      text: t("channels.activityReview", { name: a.name }),
      cta: t("channels.activityReviewCta"),
    };
  }
  // Esperando turno de render (solo corre uno a la vez en todo el servidor). Icono sin girar
  // a propósito, para distinguirlo de un vistazo del "activo" de abajo (Loader2 con spin): aquí
  // no hay nada calculándose de ESTE vídeo ahora mismo, solo está haciendo cola.
  if (a.kind === "queued") {
    return {
      tone: "amber", Icon: Clock, project_id: a.project_id,
      text: t("channels.activityQueued", { name: a.name }),
    };
  }
  return {
    tone: "blue", Icon: Loader2, spin: true, project_id: a.project_id,
    text: a.detail
      ? t("channels.activityActiveStep", { name: a.name, step: a.detail })
      : t("channels.activityActive", { name: a.name }),
    cta: t("channels.activityActiveCta"),
  };
}

// Un único aviso por canal (el más urgente/relevante), en vez de acumular banderines — mismo
// criterio de "solo lo más severo primero" que el resto de la app. Orden de prioridad: canal
// vacío > racha enfriándose > tramo de arranque (con barra de progreso) > funcionalidad sin
// probar > todo va bien (celebración).
function getInsight(ch, t) {
  if ((ch.video_count || 0) === 0) {
    return { tone: "amber", Icon: Rocket, text: t("channels.insightEmpty"), cta: t("channels.insightEmptyCta") };
  }
  const stale = daysSince(ch.last_video_at);
  if (stale != null && stale >= STALE_DAYS) {
    return { tone: "rose", Icon: Flame, text: t("channels.insightStale", { count: stale }), cta: t("channels.insightStaleCta") };
  }
  if (ch.video_count < STARTUP_MILESTONE) {
    return {
      tone: "blue", Icon: TrendingUp,
      text: t("channels.insightMilestone", { remaining: STARTUP_MILESTONE - ch.video_count, target: STARTUP_MILESTONE }),
      milestoneCount: ch.video_count, milestoneTarget: STARTUP_MILESTONE,
    };
  }
  if (!ch.recommendations || ch.recommendations.length === 0) {
    return { tone: "violet", Icon: Sparkles, text: t("channels.insightNoRecs"), cta: t("channels.insightNoRecsCta") };
  }
  return { tone: "emerald", Icon: Flame, text: t("channels.insightOnFire") };
}

// Tarjeta de un canal, reordenable por arrastre. Extraída como componente propio (en vez de
// JSX inline dentro de un .map()) porque useDragControls es un hook — solo puede llamarse
// dentro del cuerpo de un componente de verdad, no en cada iteración de un map().
function ChannelCard({ ch, i, disabled, insight, t, openCh, openUpgrade, onConfig, remove, openChannelProject }) {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={ch} id={ch.id} dragListener={false} dragControls={controls}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(i, 8) * 0.045, ease: "easeOut" }}>
      <Card data-testid={`channel-card-${ch.id}`} onClick={() => openCh(ch)}
        className={`p-0 overflow-hidden cursor-pointer transition-all duration-200 group ${disabled ? "opacity-60 border-slate-200 bg-slate-50" : "hover:border-blue-300 hover:shadow-md"}`}>
        <div className="flex items-center gap-4 p-4 sm:p-5">
          <span
            data-testid={`channel-drag-handle-${ch.id}`}
            onPointerDown={(e) => { e.stopPropagation(); controls.start(e); }}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 -ml-1 p-1.5 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none"
            title={t("channels.dragToReorder")}
          >
            <GripVertical size={20} />
          </span>
          {ch.youtube?.thumbnail ? (
            <img src={ch.youtube.thumbnail} alt={ch.name} className="rounded-2xl w-16 h-16 sm:w-20 sm:h-20 object-cover shrink-0" />
          ) : (
            <div className={`${COLORS[i % COLORS.length]} text-white rounded-2xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center text-2xl sm:text-3xl font-bold shrink-0`}>
              {(ch.name || "C").charAt(0).toUpperCase()}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{ch.name}</h3>
              {disabled && (
                <span data-testid={`channel-disabled-${ch.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full shrink-0">
                  <Lock size={11} /> {ch.active === false ? t("channels.disabled") : t("channels.blocked")}
                </span>
              )}
            </div>
            {ch.description && <p className="text-sm text-slate-500 truncate mt-0.5">{ch.description}</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
              {ch.youtube && (
                <span className="flex items-center gap-1.5 font-semibold text-red-600" data-testid={`channel-yt-subs-${ch.id}`}>
                  <Youtube size={14} /> {ch.youtube.subscribers != null ? `${ch.youtube.subscribers.toLocaleString()} ${t("channels.subs")}` : t("channels.youtubeStatsLabel")}
                </span>
              )}
              {/* Estimación de ganancias — NUNCA dato real de YouTube (ver constante DOC_RPM_*
                  arriba), solo views_month × RPM típico del sector documental. Aparte de las
                  demás (color propio) para que no se confunda con un dato real. Justo después de
                  suscriptores (pedido explícito) — es el dato que más le importa mirar al usuario
                  de un vistazo, antes que vistas/vídeos. */}
              {ch.youtube_period && (
                <span className="hidden sm:flex items-center gap-1.5 text-emerald-700 font-semibold" data-testid={`channel-earnings-estimate-${ch.id}`} title={t("channels.earningsTip")}>
                  {fmtUsd(ch.youtube_period.views_month / 1000 * DOC_RPM_MIN)}-{fmtUsd(ch.youtube_period.views_month / 1000 * DOC_RPM_MAX)}
                  {t("channels.earningsMonth")}
                </span>
              )}
              {ch.youtube && (
                <span className="hidden sm:flex items-center gap-1.5 text-slate-500" data-testid={`channel-yt-views-${ch.id}`}>
                  <Eye size={14} /> {ch.youtube.views.toLocaleString()} {t("channels.views")}
                </span>
              )}
              {/* Vistas por mes/día (últimos 30 días, ventana móvil) — solo canales con YouTube
                  CONECTADO por OAuth (ver _yt_channel_views_period en el backend); un canal solo
                  con la URL pública guardada no tiene este dato, así que el badge no aparece. */}
              {ch.youtube_period && (
                <span className="hidden sm:flex items-center gap-1.5 text-slate-500" data-testid={`channel-yt-views-month-${ch.id}`} title={t("channels.viewsMonthTip")}>
                  <CalendarDays size={14} /> {fmtCompact(ch.youtube_period.views_month)} {t("channels.viewsMonth")}
                </span>
              )}
              {ch.youtube_period && (
                <span className="hidden sm:flex items-center gap-1.5 text-slate-500" data-testid={`channel-yt-views-day-${ch.id}`} title={t("channels.viewsDayTip")}>
                  <CalendarClock size={14} /> {fmtCompact(ch.youtube_period.views_avg_day)} {t("channels.viewsDay")}
                </span>
              )}
              {/* Estadísticas de número de vídeos al final de la tarjeta (pedido explícito) —
                  YouTube antes que YTMethod, son los que de verdad están publicados. */}
              {ch.youtube && (
                <span className="hidden sm:flex items-center gap-1.5 text-slate-500" data-testid={`channel-yt-videos-${ch.id}`}>
                  <Film size={14} /> {ch.youtube.videos.toLocaleString()} {t("channels.videosLabel")}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-slate-600" title={t("channels.videoCountTip")}>
                <Video size={14} /> {t("channels.videoCount", { count: ch.video_count })}
              </span>
              {/* Material listo (guion + voz + B-roll) esperando aprobación — antes que "próxima
                  publicación programada" porque requiere una acción del usuario AHORA, mientras
                  que lo programado ya no necesita que toque nada. */}
              {!!ch.pending_review && (
                <span className="flex items-center gap-1.5 text-amber-700 font-semibold" data-testid={`channel-pending-review-${ch.id}`} title={t("channels.pendingReviewTip")}>
                  <Eye size={14} /> {t("channels.pendingReview", { count: ch.pending_review })}
                </span>
              )}
              {/* Ya subido a YouTube, pendiente de hacerse público (private + publishAt futuro) —
                  distinto de "en curso" (activity): aquí no hay nada procesándose, solo espera. */}
              {ch.next_scheduled_publish && (
                <span className="flex items-center gap-1.5 text-blue-700 font-semibold" data-testid={`channel-scheduled-publish-${ch.id}`} title={t("channels.scheduledPublishTip")}>
                  <Send size={14} /> {t("channels.scheduledPublish", { date: new Date(ch.next_scheduled_publish).toLocaleDateString(undefined, { dateStyle: "medium" }) })}
                </span>
              )}
              {/* Entradas de calendario con auto_create=True que aún no tienen proyecto — se
                  crearán solas la víspera de su fecha (ver _autocreate_calendar_videos). */}
              {ch.auto_create_pending && (
                <span className="flex items-center gap-1.5 text-violet-700 font-semibold" data-testid={`channel-autocreate-pending-${ch.id}`} title={t("channels.autoCreatePendingTip")}>
                  <Wand2 size={14} /> {t("channels.autoCreatePending", { count: ch.auto_create_pending.count, date: new Date(`${ch.auto_create_pending.next_date}T00:00:00`).toLocaleDateString(undefined, { dateStyle: "medium" }) })}
                </span>
              )}
            </div>
          </div>

          <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
            <div className="flex gap-1">
              {!disabled && (
                <>
                  <button data-testid={`edit-channel-${ch.id}`} onClick={(e) => { e.stopPropagation(); onConfig(ch.id); }} className="p-2 text-slate-400 hover:text-blue-700 rounded-lg hover:bg-slate-100" title={t("channels.configTitle")}><Settings2 size={18} /></button>
                  <button data-testid={`delete-channel-${ch.id}`} onClick={(e) => remove(ch.id, e)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-100" title={t("channels.deleteTitle")}><Trash2 size={18} /></button>
                </>
              )}
            </div>
            {disabled ? (
              ch.active === false ? (
                <button data-testid={`upgrade-channel-${ch.id}`} onClick={(e) => { e.stopPropagation(); openUpgrade && openUpgrade({ msg: t("channels.disabledUpgrade") }); }}
                  className="inline-flex items-center gap-1.5 text-blue-700 font-bold hover:gap-2 transition-all whitespace-nowrap"><Crown size={16} /> {t("channels.upgradePlan")}</button>
              ) : (
                <span className="flex items-center gap-1 text-slate-400 font-semibold whitespace-nowrap"><Lock size={16} /> {t("channels.blocked")}</span>
              )
            ) : (
              <span className="flex items-center gap-1 text-blue-700 font-semibold group-hover:gap-2 transition-all whitespace-nowrap">{t("channels.open")} <ChevronRight size={18} /></span>
            )}
          </div>
        </div>

        {insight && (
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (insight.project_id && openChannelProject) openChannelProject(ch, insight.project_id);
              else openCh(ch);
            }}
            data-testid={`channel-insight-${ch.id}`}
            className={`w-full flex items-center gap-3 px-4 sm:px-5 py-2.5 border-t text-left transition-colors ${INSIGHT_TONES[insight.tone]}`}>
            <insight.Icon size={17} className={`shrink-0 ${insight.spin ? "animate-spin" : ""}`} />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold">{insight.text}</span>
            {insight.milestoneTarget != null && (
              <div className="hidden lg:flex items-center gap-1 shrink-0" title={`${insight.milestoneCount}/${insight.milestoneTarget}`}>
                {Array.from({ length: insight.milestoneTarget }).map((_, idx) => {
                  const done = idx < insight.milestoneCount;
                  return (
                    <span key={idx}
                      className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center border ${done ? "bg-emerald-600 border-emerald-600 text-white" : "border-current opacity-40"}`}>
                      {idx + 1}
                    </span>
                  );
                })}
              </div>
            )}
            {insight.cta && <span className="hidden sm:inline text-xs font-bold underline underline-offset-2 shrink-0">{insight.cta}</span>}
          </button>
        )}
      </Card>
    </Reorder.Item>
  );
}

export default function TabCanales({ channels, channelsLoaded, loadChannels, openChannel, openChannelProject, config }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quota, refreshQuota, openUpgrade } = useQuota();
  const [wizard, setWizard] = useState(false); // creación con wizard
  const [autoShown, setAutoShown] = useState(false);
  const [selector, setSelector] = useState(false); // selector de canales activos

  const limit = quota?.channels_limit || 1;
  const activeCount = (channels || []).filter((c) => c.active !== false).length;
  // Antes de que llegue la cuota real, el "|| 1" de arriba podía hacer que alguien con más de
  // un canal activo (caso normal) viera el aviso de "tienes más canales de los permitidos" un
  // instante, hasta que refreshQuota() trajera el límite real y desapareciera solo — un falso
  // aviso parpadeando en cada carga. Sin quota todavía no se puede saber si hay exceso de
  // verdad, así que no se afirma que lo haya.
  const overLimit = !!quota && activeCount > limit;

  // Auto-lanzar el wizard solo cuando los canales han cargado y siguen vacíos
  useEffect(() => {
    if (!autoShown && channelsLoaded && Array.isArray(channels) && channels.length === 0) {
      setWizard(true);
      setAutoShown(true);
    }
  }, [channels, channelsLoaded, autoShown]);

  // Orden local arrastrable — se sincroniza con la lista real cada vez que cambia (recarga,
  // canal creado/borrado...), salvo mientras hay un reordenamiento sin confirmar todavía en el
  // backend (evita que una recarga a mitad de arrastre "deshaga" visualmente lo que se acaba de
  // soltar, antes de que loadChannels() traiga el orden ya persistido).
  const [order, setOrder] = useState(channels || []);
  const [persisting, setPersisting] = useState(false);
  const reorderTimer = useRef(null);
  useEffect(() => {
    if (!persisting) setOrder(channels || []);
  }, [channels, persisting]);
  useEffect(() => () => clearTimeout(reorderTimer.current), []);

  // onReorder de framer-motion dispara en cada paso mientras se arrastra (no solo al soltar) —
  // por eso se debounce el guardado en vez de llamar a la API en cada intercambio de posición.
  const handleReorder = (next) => {
    setOrder(next);
    setPersisting(true);
    clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        await api.post("/channels/reorder", { ids: next.map((c) => c.id) });
        await loadChannels();
      } catch (err) { toast.error(apiErr(err)); }
      finally { setPersisting(false); }
    }, 500);
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("channels.confirmDelete"))) return;
    try { await api.delete(`/channels/${id}`); toast.success(t("channels.deleted")); loadChannels(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const openCh = (ch) => {
    if (overLimit) {
      toast.info(t("channels.overLimitToast"));
      setSelector(true);
      return;
    }
    if (ch.active === false) {
      openUpgrade && openUpgrade({ msg: t("channels.disabledUpgrade") });
      return;
    }
    openChannel(ch);
  };

  const confirmActive = async (keep) => {
    await api.post("/channels/active", { keep });
    setSelector(false);
    await loadChannels();
    refreshQuota && refreshQuota();
    toast.success(t("channels.activeUpdated"));
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{t("channels.title")}</h1>
        </div>
        <button data-testid="create-channel-btn" onClick={() => setWizard(true)} className={`${btnPrimary} hidden sm:inline-flex`}>
          <Plus size={24} /> {t("channels.create")}
        </button>
      </div>

      {overLimit && (
        <div data-testid="active-channels-banner" className="rounded-2xl border-2 p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-amber-300 bg-amber-50">
          <AlertTriangle size={22} className="text-amber-600 shrink-0" />
          <p className="flex-1 text-base text-slate-700">{t("channels.bannerText", { plan: quota?.plan_label || "", limit })}</p>
          <button data-testid="choose-active-channels-btn" onClick={() => setSelector(true)} className={`${btnSecondary} whitespace-nowrap`}>{t("channels.chooseActive")}</button>
        </div>
      )}

      {!channelsLoaded ? (
        <Card className="p-10 text-center" data-testid="channels-loading">
          <Loader2 size={40} className="mx-auto text-blue-600 mb-3 animate-spin" />
          <p className="text-lg text-slate-500">{t("channels.loading")}</p>
        </Card>
      ) : channels.length === 0 ? (
        <Card className="p-10 text-center">
          <Tv size={56} className="mx-auto text-blue-600 mb-4" strokeWidth={2} />
          <h2 className="text-2xl font-semibold text-slate-800 mb-2">{t("channels.emptyTitle")}</h2>
          <p className="text-lg text-slate-600 mb-6">{t("channels.emptyDesc")}</p>
          <button data-testid="create-first-channel-btn" onClick={() => setWizard(true)} className={`${btnPrimary} mx-auto`}><Plus size={24} /> {t("channels.createFirst")}</button>
        </Card>
      ) : (
        <Reorder.Group as="div" axis="y" values={order} onReorder={handleReorder} className="space-y-3" data-testid="channels-grid">
          {order.map((ch, i) => {
            const disabled = ch.active === false || overLimit;
            const insight = !disabled ? (getActivity(ch, t) || getInsight(ch, t)) : null;
            return (
              <ChannelCard key={ch.id} ch={ch} i={i} disabled={disabled} insight={insight} t={t}
                openCh={openCh} openUpgrade={openUpgrade} onConfig={(cid) => navigate(`/app/channels/${cid}/config`)} remove={remove}
                openChannelProject={openChannelProject} />
            );
          })}
        </Reorder.Group>
      )}

      {/* En móvil el botón de crear canal no va arriba (junto al título, donde compite con el
          resto de la cabecera) sino aquí abajo, tras la lista — más alcanzable con el pulgar y
          no tapa contenido nada más entrar. En escritorio sigue arriba (botón oculto por sm:hidden
          aquí, visible por hidden sm:inline-flex en el de la cabecera). */}
      {channelsLoaded && channels.length > 0 && (
        <button data-testid="create-channel-btn-mobile" onClick={() => setWizard(true)} className={`${btnPrimary} w-full sm:hidden`}>
          <Plus size={24} /> {t("channels.create")}
        </button>
      )}

      {wizard && <ChannelWizard config={config} onClose={() => setWizard(false)} onSaved={() => { setWizard(false); loadChannels(); }} />}
      <ChannelKeepSelector
        open={selector}
        limit={limit}
        title={t("channels.selectorTitle")}
        description={t("channels.selectorDesc", { plan: quota?.plan_label || "", limit })}
        confirmLabel={t("channels.save")}
        onConfirm={confirmActive}
        onCancel={() => setSelector(false)}
      />
    </div>
  );
}
